/** Wikimedia fallback when no local file in `/brands/`. */
const wikimediaFile = (fileName: string) =>
  `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(fileName)}`;

/** URL slug under `client/public/brands/{slug}.png`. */
export const BRAND_LOGO_SLUG_BY_EN: Record<string, string> = {
  Toyota: "toyota",
  Hyundai: "hyundai",
  Nissan: "nissan",
  Kia: "kia",
  "Mercedes-Benz": "mercedes-benz",
  BMW: "bmw",
  Honda: "honda",
  Chevrolet: "chevrolet",
  Tesla: "tesla",
  BYD: "byd",
  MG: "mg",
  Peugeot: "peugeot",
  Volkswagen: "volkswagen",
  Skoda: "skoda",
  Audi: "audi",
  Ford: "ford",
  Opel: "opel",
  Seat: "seat",
  Citroen: "citroen",
  Jeep: "jeep",
  GMC: "gmc",
  Jaguar: "jaguar",
  "Land Rover": "land-rover",
  Lexus: "lexus",
  Mazda: "mazda",
  MINI: "mini",
  Subaru: "subaru",
  Mitsubishi: "mitsubishi",
  "Alfa Romeo": "alfa-romeo",
};

/** Brands with a file in `public/brands/` (use local asset first). */
const LOCAL_BRAND_SLUGS = new Set([
  "toyota",
  "hyundai",
  "byd",
  "mg",
  "mini",
  "mitsubishi",
  "opel",
  "seat",
  "skoda",
  "subaru",
  "citroen",
  "chevrolet",
  "peugeot",
]);

/** Filter grid: slightly larger logo artwork for these marks. */
const LARGER_LOGO_BRANDS_EN = new Set([
  "Mitsubishi",
  "Opel",
  "Chevrolet",
  "Toyota",
  "Hyundai",
  "Subaru",
]);

/** Compact PNG artwork · needs the biggest slot in the grid. */
const EXTRA_LARGE_LOGO_BRANDS_EN = new Set(["BYD", "Citroen", "MINI"]);

/** Wide horizontal wordmarks · cap width so they match round badge logos. */
const WIDE_WORDMARK_LOGO_BRANDS_EN = new Set(["Kia"]);

function matchesBrandSet(nameEn: string, set: Set<string>): boolean {
  const t = nameEn.trim();
  if (!t) return false;
  const lower = t.toLowerCase();
  const first = t.split(/\s+/)[0].toLowerCase();
  for (const item of set) {
    const key = item.toLowerCase();
    if (key === lower || key === first) return true;
  }
  return false;
}

const WIKIMEDIA_FALLBACK_BY_EN: Record<string, string> = {
  BMW: wikimediaFile("BMW_Roundel.svg"),
  Mercedes: wikimediaFile("Mercedes-Logo.svg"),
  "Mercedes-Benz": wikimediaFile("Mercedes-Logo.svg"),
  Audi: wikimediaFile("Audi-Logo_2016.svg"),
  Volkswagen: wikimediaFile("Volkswagen_logo_2019.svg"),
  Porsche: wikimediaFile("Porsche Wordmark Logo Black.svg"),
  Toyota: wikimediaFile("Toyota_carlogo.svg"),
  Honda: wikimediaFile("Honda.svg"),
  Nissan: wikimediaFile("Nissan_2020_logo.svg"),
  Hyundai: wikimediaFile("Hyundai_Motor_Company_logo.svg"),
  Kia: wikimediaFile("KIA_logo3.svg"),
  Mazda: wikimediaFile("Mazda logo.svg"),
  Ford: wikimediaFile("Ford_logo_flat.svg"),
  Tesla: wikimediaFile("Tesla_logo.png"),
  Subaru: wikimediaFile("Subaru_logo_2.svg"),
  Lexus: wikimediaFile("Lexus.svg"),
  Infiniti: wikimediaFile("Infiniti_logo.svg"),
  Volvo: wikimediaFile("Volvo-Iron-Mark-Black.svg"),
  Jaguar: wikimediaFile("Jaguar wordmark 2021.svg"),
  Land: wikimediaFile("Land_Rover_2023.svg"),
  "Land Rover": wikimediaFile("Land_Rover_2023.svg"),
  Jeep: wikimediaFile("Jeep_logo.svg"),
  Renault: wikimediaFile("Renault_2021_Text_Logo.svg"),
  Peugeot: wikimediaFile("Peugeot_logo.svg"),
  Fiat: wikimediaFile("Fiat_Automobiles_logo.svg"),
  BYD: wikimediaFile("BYD_Auto_logo.svg"),
  MG: wikimediaFile("MG_Motor_UK_Logo.svg"),
  Skoda: wikimediaFile("Skoda_Auto_logo_2016.svg"),
  Opel: wikimediaFile("Opel_logo_2020.svg"),
  Seat: wikimediaFile("SEAT_logo_2017.svg"),
  Citroen: wikimediaFile("Citroen_logo_2022.svg"),
  GMC: wikimediaFile("GMC_logo.svg"),
  MINI: wikimediaFile("MINI_logo.svg"),
  Mitsubishi: wikimediaFile("Mitsubishi_motors_logo.svg"),
  "Alfa Romeo": wikimediaFile("Alfa_Romeo_logo.svg"),
  Alfa: wikimediaFile("Alfa_Romeo_logo.svg"),
};

const BRAND_FALLBACK_HEX: Record<string, string> = {
  BMW: "#1C69D4",
  "Mercedes-Benz": "#222222",
  Audi: "#BB0A30",
  Volkswagen: "#001E50",
  Toyota: "#EB0A1E",
  Tesla: "#CC0000",
  BYD: "#1a1a1a",
  MG: "#C4132A",
  "Alfa Romeo": "#981E32",
};

function brandSlug(nameEn: string): string | undefined {
  const t = nameEn.trim();
  if (!t) return undefined;
  if (BRAND_LOGO_SLUG_BY_EN[t]) return BRAND_LOGO_SLUG_BY_EN[t];
  return BRAND_LOGO_SLUG_BY_EN[t.split(/\s+/)[0]];
}

export function localBrandLogoPath(nameEn: string): string | undefined {
  const slug = brandSlug(nameEn);
  if (!slug || !LOCAL_BRAND_SLUGS.has(slug)) return undefined;
  return `/brands/${slug}.png`;
}

export function logoUrlForBrandEn(nameEn: string, dbLogo?: string | null): string | undefined {
  if (dbLogo?.trim()) return dbLogo.trim();

  const local = localBrandLogoPath(nameEn);
  if (local) return local;

  const t = nameEn.trim();
  if (!t) return undefined;
  if (WIKIMEDIA_FALLBACK_BY_EN[t]) return WIKIMEDIA_FALLBACK_BY_EN[t];
  const first = t.split(/\s+/)[0];
  return WIKIMEDIA_FALLBACK_BY_EN[first];
}

export function fallbackHexForBrandEn(nameEn: string): string {
  const t = nameEn.trim();
  return BRAND_FALLBACK_HEX[t] ?? BRAND_FALLBACK_HEX[t.split(/\s+/)[0]] ?? "#6b7280";
}

export function brandLogoContainerClass(nameEn: string): string {
  const base =
    "flex w-full items-center justify-center overflow-hidden rounded-lg bg-gray-50/90 px-0.5";
  if (matchesBrandSet(nameEn, EXTRA_LARGE_LOGO_BRANDS_EN)) return `${base} h-14`;
  if (matchesBrandSet(nameEn, LARGER_LOGO_BRANDS_EN)) return `${base} h-12`;
  return `${base} h-10`;
}

export function brandLogoImageClass(nameEn: string): string {
  const base = "max-w-full object-contain drop-shadow-sm";
  if (matchesBrandSet(nameEn, EXTRA_LARGE_LOGO_BRANDS_EN)) {
    return `${base} max-h-[3.25rem] min-h-[2.5rem] scale-[1.4]`;
  }
  if (matchesBrandSet(nameEn, LARGER_LOGO_BRANDS_EN)) return `${base} max-h-11 scale-110`;
  if (matchesBrandSet(nameEn, WIDE_WORDMARK_LOGO_BRANDS_EN)) {
    return `${base} max-h-8 max-w-[4.5rem] w-full`;
  }
  return `${base} max-h-9`;
}

/** Register additional slugs that exist under `public/brands/`. */
export function registerLocalBrandLogoSlug(slug: string): void {
  LOCAL_BRAND_SLUGS.add(slug.toLowerCase());
}
